import { set } from '@ember/object';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import Cookies from 'js-cookie';
import { HOTEL_SORT_BY, KPIS } from 'lumen/utils/constants/ri-constant-values';
import {
  sortSegmentationNameLeadtime,
  sortSegmentationNamePickup,
  sortSegmentationValueByKpi,
  sortSegmentationValueChange,
} from 'lumen/utils/revenue-insight/sort-functions';

const MESSAGE_DISPLAY_COOKIE = 'ota-hide-messages';

/**
 * Sort a segmentation according to the specific sort needed
 * This is a recursive function that will go through all levels of segmentations
 * @param  { array } segmentationObjects : all top level segmentation objects
 * @param  { string } segmentationName   : id of the segmentation
 * @return { array }                     : return the segmentation that have been sorted. All children should be sorted as well
 */
export function sortValues(
  segmentationObjects,
  segmentationName,
  kpi,
  parsedSegmentationFilters,
  extraOptions = {},
) {
  let hasBlockSpecificSort = false;
  let sortedSegmentationObjects = segmentationObjects;
  const containsSelectedSegmentationValue =
    parsedSegmentationFilters[segmentationName];

  if (segmentationName === RI_BREAKDOWNS.PICKUP) {
    // in the pickup block we want an ASC sort of the nb of days (nb of days is already stored in segmentationSortValue)
    sortedSegmentationObjects = sortSegmentationNamePickup(
      segmentationObjects,
      kpi,
      'bob',
      'asc',
    );

    hasBlockSpecificSort = true;
  } else if (
    segmentationName === RI_BREAKDOWNS.BOOKING_LEAD_TIME ||
    segmentationName === RI_BREAKDOWNS.CANCELLATION_LEAD_TIME
  ) {
    sortedSegmentationObjects = sortSegmentationNameLeadtime(
      segmentationObjects,
      kpi,
      'bob',
      'asc',
    );

    hasBlockSpecificSort = true;
  } else if (segmentationName === RI_BREAKDOWNS.SUBSCRIPTION_ID) {
    const { sortBy, sortType, sortOrder } = extraOptions;
    if (sortBy === HOTEL_SORT_BY.CHANGE) {
      sortedSegmentationObjects = sortSegmentationValueChange(
        segmentationObjects,
        kpi,
        sortType,
        sortOrder,
      );
    } else {
      sortedSegmentationObjects = sortSegmentationValueByKpi(
        segmentationObjects,
        kpi,
        containsSelectedSegmentationValue,
        sortType,
        sortOrder,
      );
    }
    hasBlockSpecificSort = true;
  }

  if (!hasBlockSpecificSort) {
    const { sortType } = extraOptions;
    let kpiForBlockSpecificSort = kpi;

    if (kpi === KPIS.ADR) {
      // When the KPI ADR is selected, we need to sort the segmentation objects based on the Revenue value
      kpiForBlockSpecificSort = KPIS.REVENUE;
    }

    sortedSegmentationObjects = sortSegmentationValueByKpi(
      segmentationObjects,
      kpiForBlockSpecificSort,
      containsSelectedSegmentationValue,
      sortType,
    );
  }

  // loops over all parents and sorts the children
  sortedSegmentationObjects.forEach((parent) => {
    if (parent.children && !parent.isDestroyed) {
      // Will be called recursively so childrens children will be sorted as well
      // Use set to avoid setting a tracked property
      set(
        parent,
        'children',
        sortValues(
          parent.children,
          segmentationName,
          kpi,
          parsedSegmentationFilters,
          extraOptions,
        ),
      );
    }
  });

  return sortedSegmentationObjects;
}

/**
 * Creates a new cookie if one does not exist yet
 * Or updates the cookie if it does
 * Will result in the message being hidden for one year
 */
export function toggleFilterMessageCookie(doNotDisplayRIFilterMessage) {
  if (doNotDisplayRIFilterMessage) {
    Cookies.set(MESSAGE_DISPLAY_COOKIE, true, { expires: 365 });
  } else {
    Cookies.remove(MESSAGE_DISPLAY_COOKIE);
  }
}

/**
 * Adds rates data to the stayDate data used in the stayDate chart
 * @param {Array} stayDateData The array containing stayDate data used by the stayDate chart
 */
export function addRatesToStayDates(stayDateData, mappedRates) {
  return stayDateData.map((stayDateSegmentation) => {
    if (mappedRates[stayDateSegmentation.segmentationValue]) {
      const copiedStayDateSegmentation = stayDateSegmentation;
      const ratesForCurrentStayDate =
        mappedRates[stayDateSegmentation.segmentationValue].rates;
      const dayForCurrentStayDate =
        mappedRates[stayDateSegmentation.segmentationValue].day;

      copiedStayDateSegmentation.rates = ratesForCurrentStayDate;
      copiedStayDateSegmentation.day = dayForCurrentStayDate;

      return copiedStayDateSegmentation;
    }

    return stayDateSegmentation;
  });
}

/**
 * Creates the structure used in segmentation-block
 * @param  { string } segmentationKey : id of segment
 * @return { object}                  : mapped segmentation from the API
 */
export function createSegmentationBlockStructure(
  segmentationKey,
  segmentationObjects,
) {
  const segmentationBlockStructure = {
    segmentationName: segmentationKey,
    segmentationObjects,
  };

  return segmentationBlockStructure;
}

export function getSdlyEnd(start, sdlyStart, referencePeriod) {
  const isFebInLeapYear = start.isLeapYear() && start.month() === 1;
  let sdlyEnd = sdlyStart.add(referencePeriod, 'day');

  // This was added because you were comparing feb 29 with march 1st the year before. This was causing weird results so we decided to remove it
  if (isFebInLeapYear) {
    sdlyEnd = sdlyEnd.subtract(1, 'day');
  }

  // SH-13141: Leap year issue in reference period - when comparing to SDLY with reference being a leap year month the leap day is also included
  // Extend the period if either the leap year day is in it, or if we're looking at the whole month of Februari
  if (sdlyStart.isLeapYear()) {
    const dayBeforeLeapDay = sdlyStart.month(1).date(28);
    const startOfFebLeapYear = sdlyStart.month(1).date(1);
    if (
      sdlyStart.isSameOrBefore(dayBeforeLeapDay) &&
      (sdlyEnd.isAfter(dayBeforeLeapDay) ||
        (sdlyEnd.isSame(dayBeforeLeapDay) &&
          sdlyStart.isSameOrBefore(startOfFebLeapYear)))
    ) {
      sdlyEnd = sdlyEnd.add(1, 'day');
    }
  }

  return sdlyEnd;
}
